import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useAttendanceConceptDetailPanel } from '@/hooks/attendance/concept/useAttendanceConceptDetailPanel';
import { useEditAttendanceConceptPanel } from '@/hooks/attendance/concept/useEditAttendanceConceptPanel';


interface ConceptDetailPanelProps {
  conceptId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const ConceptDetailPanel: React.FC<ConceptDetailPanelProps> = ({
  conceptId,
  onSaved,
  readOnly,
}) => {
  const { t: translate } = useTranslation();
  const { concept } = useAttendanceConceptDetailPanel({ conceptId });

  const {
    handlingValue,
    calculationFormulaValue,
    obtentionFormulaValue,
    handlingOptions,
    formulaOptions,
    isSubmitting,
    isReady,
    isReadOnly,
    errors,
    isSubmitted,
    handleHandlingChange,
    handleCalculationFormulaChange,
    handleObtentionFormulaChange,
    handleSubmit,
  } = useEditAttendanceConceptPanel({ concept, onSaved, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="concept-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:attendanceConcept.fields.id')}
          value={concept?.id !== undefined ? String(concept.id) : ''}
          disabled
          readOnly
          data-testid="concept-detail-id"
        />
        <LabelInput
          label={translate('attendance:attendanceConcept.fields.name')}
          value={concept?.name ?? ''}
          disabled
          readOnly
          data-testid="concept-detail-name"
        />
        <LabelInput
          label={translate('attendance:attendanceConcept.fields.code')}
          value={concept?.code ?? ''}
          disabled
          readOnly
          data-testid="concept-detail-code"
        />
        <LabelInput
          label={translate('attendance:attendanceConcept.fields.type')}
          value={concept?.type ?? ''}
          disabled
          readOnly
          data-testid="concept-detail-type"
        />

        {isReadOnly ? (
          <LabelInput
            label={translate('attendance:attendanceConcept.fields.handling')}
            value={concept?.handling ?? ''}
            disabled
            readOnly
            data-testid="concept-detail-handling"
          />
        ) : (
          <Selector
            label={translate('attendance:attendanceConcept.fields.handling')}
            placeholder={translate('attendance:attendanceConcept.placeholders.handling')}
            options={handlingOptions}
            value={handlingValue || undefined}
            onChange={handleHandlingChange}
            disabled={isSubmitting}
            color={errors.handlingId && isSubmitted ? 'error' : 'default'}
            supportingText={
              errors.handlingId?.message && isSubmitted
                ? translate(errors.handlingId.message)
                : undefined
            }
            required
            data-testid="concept-edit-handling"
          />
        )}

        {isReadOnly ? (
          <LabelInput
            label={translate('attendance:attendanceConcept.fields.calculationFormula')}
            value={concept?.calculationFormula ?? ''}
            disabled
            readOnly
            data-testid="concept-detail-calculation-formula"
          />
        ) : (
          <Selector
            label={translate('attendance:attendanceConcept.fields.calculationFormula')}
            placeholder={translate('attendance:attendanceConcept.placeholders.calculationFormula')}
            options={formulaOptions}
            value={calculationFormulaValue || undefined}
            onChange={handleCalculationFormulaChange}
            disabled={isSubmitting}
            color={errors.calculationFormulaId && isSubmitted ? 'error' : 'default'}
            supportingText={
              errors.calculationFormulaId?.message && isSubmitted
                ? translate(errors.calculationFormulaId.message)
                : undefined
            }
            required
            data-testid="concept-edit-calculation-formula"
          />
        )}

        {isReadOnly ? (
          <LabelInput
            label={translate('attendance:attendanceConcept.fields.obtentionFormula')}
            value={concept?.obtentionFormula ?? ''}
            disabled
            readOnly
            data-testid="concept-detail-obtention-formula"
          />
        ) : (
          <Selector
            label={translate('attendance:attendanceConcept.fields.obtentionFormula')}
            placeholder={translate('attendance:attendanceConcept.placeholders.obtentionFormula')}
            options={formulaOptions}
            value={obtentionFormulaValue || undefined}
            onChange={handleObtentionFormulaChange}
            disabled={isSubmitting}
            color={errors.obtentionFormulaId && isSubmitted ? 'error' : 'default'}
            supportingText={
              errors.obtentionFormulaId?.message && isSubmitted
                ? translate(errors.obtentionFormulaId.message)
                : undefined
            }
            required
            data-testid="concept-edit-obtention-formula"
          />
        )}
      </SidePanelFormLayout.Fields>

      {!isReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="edit">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate('common:actions.save')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default ConceptDetailPanel;
